/**
 * Integrytas Group - Base App (MOBILE)
 * Page: GSI.INDEXER — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/GsiIndexerPage.tsx. A DISTINCT
 * presentation (KPI tiles for live index/worker stats + a scannable card list of
 * the indexable-entity registry + bottom-sheet detail + a confirm sheet for the
 * one-click reindex) over the SAME data/logic layer only:
 *   - reads via apiDb (gsi_indexable_entity, gsi_cursor 'main', gsi_index count),
 *   - the enable/disable write on an entity routes through the Common Routine
 *     (routedUpdate) — §2 single write path; never a direct apiDb write,
 *   - reindex uses the module's own sanctioned RPC (gsi_reindex), which replays
 *     the audit stream under the caller's JWT, exactly like the desktop page.
 * It never re-renders the desktop grid; §16.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, Power, RefreshCw, Database, AlertTriangle } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedUpdate } from '../../lib/routed_writes';
import { useFormatDateTime } from '../../lib/format_date';
import { MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'GSI.INDEXER';

interface MobileEntityRow {
  id: number;
  entity_name: string;
  page_code: string | null;
  title_field: string | null;
  text_fields: string[];
  workspace_field: string | null;
  weight: number;
  is_active: 'Y' | 'N';
}

interface CursorRow {
  last_audit_log_id: number | null;
  last_run_at: string | null;
  processed_total: number | null;
}

type StatusFacet = 'all' | 'active' | 'inactive';

type RawEntity = {
  gsi_indexable_entity_id: number;
  entity_name: string;
  page_code: string | null;
  title_field: string | null;
  text_fields: unknown;
  workspace_field: string | null;
  weight: number;
  is_active: 'Y' | 'N';
};

type RawCursor = {
  last_audit_log_id: number | null;
  last_run_at: string | null;
  processed_total: number | null;
};

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
}

export function MobileGsiIndexerPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [rows, setRows] = useState<MobileEntityRow[]>([]);
  const [cursor, setCursor] = useState<CursorRow | null>(null);
  const [indexCount, setIndexCount] = useState<number | null>(null);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [selected, setSelected] = useState<MobileEntityRow | null>(null);
  const [confirmReindex, setConfirmReindex] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const [e, c, n] = await Promise.all([
        apiDb
          .from('gsi_indexable_entity')
          .select('gsi_indexable_entity_id, entity_name, page_code, title_field, text_fields, workspace_field, weight, is_active')
          .order('entity_name'),
        apiDb
          .from('gsi_cursor')
          .select('last_audit_log_id, last_run_at, processed_total')
          .eq('cursor_name', 'main')
          .maybeSingle(),
        apiDb.from('gsi_index').select('gsi_index_id', { head: true, count: 'exact' }),
      ]);
      if (e.error) throw e.error;

      const list: MobileEntityRow[] = ((e.data ?? []) as RawEntity[]).map((r) => ({
        id: r.gsi_indexable_entity_id,
        entity_name: r.entity_name,
        page_code: r.page_code,
        title_field: r.title_field,
        text_fields: toStringArray(r.text_fields),
        workspace_field: r.workspace_field,
        weight: r.weight,
        is_active: r.is_active,
      }));
      setRows(list);
      setCursor((c.data ?? null) as RawCursor | null);
      setIndexCount(n.count ?? null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Best-effort load on mount; the registry rows are technical config (entity /
  // page codes), so nothing here follows the active locale.
  useEffect(() => { void reload(); }, []);

  const activeCount = useMemo(() => rows.filter((r) => r.is_active === 'Y').length, [rows]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (!q) return true;
      return (
        r.entity_name.toLowerCase().includes(q) ||
        (r.page_code ?? '').toLowerCase().includes(q)
      );
    });
  }, [rows, filter, statusFacet]);

  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.gsi.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const fmtNum = (v: number | null): string => (v == null ? '—' : v.toLocaleString());
  const lastRun = cursor?.last_run_at ? formatDateTime(cursor.last_run_at) : '—';

  const toggleActive = async (r: MobileEntityRow) => {
    setActionBusy(true);
    setError(null);
    const next = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'gsi_indexable_entity', 'gsi_indexable_entity_id', r.id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  const runReindex = async () => {
    setActionBusy(true);
    setError(null);
    try {
      // Sanctioned module RPC — replays the audit stream under the caller's JWT,
      // clearing and repopulating the index (mirrors the desktop page).
      const { data, error: e } = await apiDb.rpc<{ ok: boolean; error_code?: string }>('gsi_reindex');
      if (e) throw e;
      if (data && data.ok === false) {
        setError(data.error_code ?? t('mobile.gsi.reindex_refused', 'Reindex refused.'));
      }
      setConfirmReindex(false);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.gsi.title', 'Search Index')}
        description={t('mobile.gsi.description', 'Live indexer health and the registry of indexable entities.')}
      />

      <div className="m-kpis">
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.gsi.kpi.indexed', 'Indexed records')}</div>
          <div className="m-kpi-v">{fmtNum(indexCount)}</div>
        </div>
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.gsi.kpi.processed', 'Total processed')}</div>
          <div className="m-kpi-v">{fmtNum(cursor?.processed_total ?? null)}</div>
        </div>
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.gsi.kpi.cursor', 'Audit cursor')}</div>
          <div className="m-kpi-v">{fmtNum(cursor?.last_audit_log_id ?? null)}</div>
        </div>
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.gsi.kpi.active_entities', 'Active entities')}</div>
          <div className="m-kpi-v">{busy && rows.length === 0 ? '—' : activeCount.toLocaleString()}</div>
        </div>
      </div>

      <div className="m-card">
        <div className="m-field">
          <span className="m-k">{t('mobile.gsi.last_run', 'Last run')}</span>
          <span className="m-v">{lastRun}</span>
        </div>
        <div className="m-actionbar">
          <button
            type="button"
            className="m-btn m-btn-primary"
            disabled={actionBusy || busy}
            onClick={() => setConfirmReindex(true)}
          >
            <RefreshCw size={16} />
            {t('mobile.gsi.reindex', 'Reindex')}
          </button>
          <button
            type="button"
            className="m-btn m-btn-ghost"
            disabled={actionBusy || busy}
            onClick={() => void reload()}
            aria-label={t('mobile.gsi.refresh', 'Refresh')}
          >
            <Search size={16} />
          </button>
        </div>
      </div>

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.gsi.search', 'Search indexable entities')}
          aria-label={t('mobile.gsi.search', 'Search indexable entities')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.gsi.none', 'No entities match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.id}
              lead={<Database />}
              leadTone={r.is_active === 'Y' ? 'green' : 'slate'}
              title={r.entity_name || t('mobile.gsi.unnamed', '(unnamed)')}
              subtitle={`${r.page_code ?? t('mobile.gsi.no_page', 'no page gate')} · ${t('mobile.gsi.weight', 'weight {{n}}', { n: r.weight })}`}
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.gsi.detail', 'Entity detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Database size={20} /></span>
              <span>
                <div className="m-nm">{selected.entity_name || t('mobile.gsi.unnamed', '(unnamed)')}</div>
                <div className="m-em">{selected.page_code ?? t('mobile.gsi.no_page', 'no page gate')}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.gsi.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.gsi.title_field', 'Title field')} value={selected.title_field ?? t('common.none', 'None')} />
              <MobileDetailRow
                label={t('mobile.gsi.text_fields', 'Text fields')}
                value={selected.text_fields.length === 0 ? t('common.none', 'None') : selected.text_fields.join(', ')}
              />
              <MobileDetailRow
                label={t('mobile.gsi.workspace_field', 'Workspace field')}
                value={selected.workspace_field ?? t('mobile.gsi.audit_workspace', 'Audit workspace')}
              />
              <MobileDetailRow label={t('mobile.gsi.weight_label', 'Weight')} value={selected.weight} />
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-primary')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('mobile.gsi.disable', 'Disable') : t('mobile.gsi.enable', 'Enable')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Reindex confirmation sheet */}
      <div className={'m-scrim' + (confirmReindex ? ' m-open' : '')} onClick={() => { if (!actionBusy) setConfirmReindex(false); }} />
      <div className={'m-sheet' + (confirmReindex ? ' m-open' : '')} aria-label={t('mobile.gsi.reindex_title', 'Reindex search')}>
        <div className="m-grabber" />
        {confirmReindex && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><AlertTriangle size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.gsi.reindex_title', 'Reindex search')}</div>
                <div className="m-em">{t('mobile.gsi.reindex_scope', 'Replays the audit stream')}</div>
              </span>
            </div>
            <div className="m-fieldcard">
              <div className="m-field" style={{ gridTemplateColumns: '1fr' }}>
                <span className="m-k" style={{ textAlign: 'left' }}>
                  {t('mobile.gsi.reindex_confirm', 'Rebuild the entire search index? Existing index rows are cleared and repopulated by the worker from the audit stream.')}
                </span>
              </div>
            </div>
            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={actionBusy} onClick={() => void runReindex()}>
                <RefreshCw size={16} />
                {actionBusy ? t('mobile.gsi.reindexing', 'Reindexing…') : t('mobile.gsi.reindex_go', 'Rebuild index')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setConfirmReindex(false)} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
